import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription, take } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { BlogLang, BlogsService } from '../../../../services/blog';

type BlogCategoryId = 'career' | 'cv' | 'travel' | 'interview';

interface StaticBlogPost {
  id: number;
  slug: string;
  iconImg: string;
  category: BlogCategoryId;
  categoryKey: string;
  titleKey: string;
  excerptKey: string;
  contentKey: string;
  dateKey: string;
  readTimeKey: string;
  tagsKeys: string[];
}

interface BlogDetailsView {
  id: string | number;
  slug: string;
  title: string;
  excerpt: string;
  categoryLabel: string;
  image: string;
  dateLabel: string;
  readTimeLabel: string;
  tags: string[];
  contentHtml: string;
  paragraphs: string[];
  isApi: boolean;
}

@Component({
  selector: 'app-blogdetails',
  templateUrl: './blogdetails.component.html',
  styleUrl: './blogdetails.component.scss',
})
export class BlogdetailsComponent implements OnInit, OnDestroy {
  blog?: BlogDetailsView;
  loading = false;
  error = '';

  isAr = false;
  currentLang: BlogLang = 'en';

  private isBrowser: boolean;
  private routeSub?: Subscription;
  private langSub?: Subscription;
  private currentSlug = '';

  private staticBlogs: StaticBlogPost[] = [
    {
      id: 1,
      slug: 'how-to-build-a-strong-cv',
      iconImg: 'assets/as/30.webp',
      category: 'cv',
      categoryKey: 'atw.blogs.posts.post1.category',
      titleKey: 'atw.blogs.posts.post1.title',
      excerptKey: 'atw.blogs.posts.post1.excerpt',
      contentKey: 'atw.blogs.posts.post1.content',
      dateKey: 'atw.blogs.posts.post1.date',
      readTimeKey: 'atw.blogs.posts.post1.readTime',
      tagsKeys: [
        'atw.blogs.tags.cv',
        'atw.blogs.tags.keywords',
        'atw.blogs.tags.jobs',
      ],
    },
    {
      id: 2,
      slug: 'prepare-for-your-first-interview',
      iconImg: 'assets/as/17.webp',
      category: 'interview',
      categoryKey: 'atw.blogs.posts.post2.category',
      titleKey: 'atw.blogs.posts.post2.title',
      excerptKey: 'atw.blogs.posts.post2.excerpt',
      contentKey: 'atw.blogs.posts.post2.content',
      dateKey: 'atw.blogs.posts.post2.date',
      readTimeKey: 'atw.blogs.posts.post2.readTime',
      tagsKeys: [
        'atw.blogs.tags.interview',
        'atw.blogs.tags.confidence',
        'atw.blogs.tags.tips',
      ],
    },
    {
      id: 3,
      slug: 'top-career-mistakes-to-avoid',
      iconImg: 'assets/as/51.webp',
      category: 'career',
      categoryKey: 'atw.blogs.posts.post3.category',
      titleKey: 'atw.blogs.posts.post3.title',
      excerptKey: 'atw.blogs.posts.post3.excerpt',
      contentKey: 'atw.blogs.posts.post3.content',
      dateKey: 'atw.blogs.posts.post3.date',
      readTimeKey: 'atw.blogs.posts.post3.readTime',
      tagsKeys: [
        'atw.blogs.tags.career',
        'atw.blogs.tags.growth',
        'atw.blogs.tags.planning',
      ],
    },
    {
      id: 4,
      slug: 'working-abroad-checklist',
      iconImg: 'assets/as/50.webp',
      category: 'travel',
      categoryKey: 'atw.blogs.posts.post4.category',
      titleKey: 'atw.blogs.posts.post4.title',
      excerptKey: 'atw.blogs.posts.post4.excerpt',
      contentKey: 'atw.blogs.posts.post4.content',
      dateKey: 'atw.blogs.posts.post4.date',
      readTimeKey: 'atw.blogs.posts.post4.readTime',
      tagsKeys: [
        'atw.blogs.tags.travel',
        'atw.blogs.tags.documents',
        'atw.blogs.tags.visa',
      ],
    },
    {
      id: 5,
      slug: 'how-to-match-your-skills-with-jobs',
      iconImg: 'assets/as/52.webp',
      category: 'career',
      categoryKey: 'atw.blogs.posts.post5.category',
      titleKey: 'atw.blogs.posts.post5.title',
      excerptKey: 'atw.blogs.posts.post5.excerpt',
      contentKey: 'atw.blogs.posts.post5.content',
      dateKey: 'atw.blogs.posts.post5.date',
      readTimeKey: 'atw.blogs.posts.post5.readTime',
      tagsKeys: [
        'atw.blogs.tags.skills',
        'atw.blogs.tags.matching',
        'atw.blogs.tags.jobs',
      ],
    },
    {
      id: 6,
      slug: 'cv-keywords-that-help-you-get-noticed',
      iconImg: 'assets/as/2.webp',
      category: 'cv',
      categoryKey: 'atw.blogs.posts.post6.category',
      titleKey: 'atw.blogs.posts.post6.title',
      excerptKey: 'atw.blogs.posts.post6.excerpt',
      contentKey: 'atw.blogs.posts.post6.content',
      dateKey: 'atw.blogs.posts.post6.date',
      readTimeKey: 'atw.blogs.posts.post6.readTime',
      tagsKeys: [
        'atw.blogs.tags.cv',
        'atw.blogs.tags.keywords',
        'atw.blogs.tags.ats',
      ],
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private blogsService: BlogsService,
    private transloco: TranslocoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.setCurrentLang();

    this.routeSub = this.route.paramMap.subscribe((params) => {
      this.currentSlug = params.get('id') || '';
      this.loadBlog();
    });

    this.langSub = this.transloco.langChanges$.subscribe((lang) => {
      const oldLang = this.currentLang;
      this.setCurrentLang(lang as BlogLang);

      if (oldLang !== this.currentLang && this.currentSlug) {
        this.loadBlog();
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.langSub?.unsubscribe();
  }

  loadBlog(): void {
    if (!this.currentSlug) {
      this.showNotFound();
      return;
    }

    this.loading = true;
    this.error = '';
    this.blog = undefined;

    this.transloco
      .load(this.currentLang)
      .pipe(take(1))
      .subscribe({
        next: () => this.loadBlogAfterTranslations(),
        error: () => this.loadBlogAfterTranslations(),
      });
  }

  goBackToBlogs(): void {
    this.router.navigate(['/blogs']);
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/as/30.webp';
  }

  private loadBlogAfterTranslations(): void {
    const staticBlog = this.findStaticBlog(this.currentSlug);

    if (staticBlog) {
      this.blog = this.mapStaticBlog(staticBlog);
      this.loading = false;
      return;
    }

    this.blogsService.getBlogDetails(this.currentSlug, this.currentLang).subscribe({
      next: (res) => {
        const apiBlog = this.extractBlog(res);

        if (!apiBlog) {
          this.showNotFound();
          return;
        }

        this.blog = this.mapApiBlog(apiBlog);
        this.loading = false;
      },
      error: (err) => {
        console.error('Blog details API Error:', err);
        this.showNotFound();
      },
    });
  }

  private showNotFound(): void {
    this.blog = undefined;
    this.loading = false;
    this.error = this.t('atw.blogs.details.notFound');
  }

  private findStaticBlog(slug: string): StaticBlogPost | undefined {
    const normalizedSlug = String(slug).replace(/^static-/, '');

    return this.staticBlogs.find(
      (blog) =>
        blog.slug === slug ||
        String(blog.id) === normalizedSlug ||
        `static-${blog.id}` === slug
    );
  }

  private mapStaticBlog(blog: StaticBlogPost): BlogDetailsView {
    const content = this.t(blog.contentKey);
    const paragraphs = this.splitParagraphs(content);

    return {
      id: `static-${blog.id}`,
      slug: blog.slug,
      title: this.t(blog.titleKey),
      excerpt: this.t(blog.excerptKey),
      categoryLabel: this.t(blog.categoryKey),
      image: blog.iconImg,
      dateLabel: this.t(blog.dateKey),
      readTimeLabel: this.t(blog.readTimeKey),
      tags: blog.tagsKeys.map((tagKey) => this.t(tagKey)),
      contentHtml: '',
      paragraphs,
      isApi: false,
    };
  }

  private mapApiBlog(blog: any): BlogDetailsView {
    const category = this.normalizeCategory(blog.category);
    const contentHtml =
      this.getLocalizedField(blog, 'content') ||
      this.getLocalizedField(blog, 'body') ||
      this.getLocalizedField(blog, 'description') ||
      this.getLocalizedField(blog, 'excerpt');

    return {
      id: blog.id || this.currentSlug,
      slug: blog.slug || blog.slug_en || blog.slug_ar || String(blog.id),
      title:
        this.getLocalizedField(blog, 'title') ||
        (this.isAr ? 'مقال بدون عنوان' : 'Untitled blog'),
      excerpt: this.getExcerpt(blog),
      categoryLabel: this.getCategoryLabel(blog, category),
      image: this.getBlogImage(blog),
      dateLabel: this.formatDate(blog.published_at || blog.created_at),
      readTimeLabel: this.getLocalizedField(blog, 'read_time'),
      tags: this.getTags(blog),
      contentHtml,
      paragraphs: [],
      isApi: true,
    };
  }

  private extractBlog(res: any): any {
    if (!res) {
      return null;
    }

    if (res.data?.data && !Array.isArray(res.data.data)) {
      return res.data.data;
    }

    if (res.data && !Array.isArray(res.data)) {
      return res.data;
    }

    if (res.blog && !Array.isArray(res.blog)) {
      return res.blog;
    }

    return Array.isArray(res) ? res[0] : res;
  }

  private getLocalizedField(blog: any, field: string): string {
    if (!blog) {
      return '';
    }

    if (this.currentLang === 'ar') {
      return blog[`${field}_ar`] || blog[field] || blog[`${field}_en`] || '';
    }

    return blog[`${field}_en`] || blog[field] || blog[`${field}_ar`] || '';
  }

  private getExcerpt(blog: any): string {
    const excerpt = this.getLocalizedField(blog, 'excerpt');

    if (excerpt) {
      return this.stripHtml(excerpt);
    }

    const content = this.getLocalizedField(blog, 'content');

    return this.stripHtml(content).slice(0, 220);
  }

  private getCategoryLabel(blog: any, category: BlogCategoryId): string {
    const apiLabel =
      this.currentLang === 'ar'
        ? blog.category_ar || blog.category_en
        : blog.category_en || blog.category_ar;

    if (apiLabel) {
      return apiLabel;
    }

    const key = `atw.blogs.categories.${category}`;
    const translated = this.t(key);

    return translated && translated !== key ? translated : category;
  }

  private normalizeCategory(category: any): BlogCategoryId {
    const value = String(category || '').trim().toLowerCase();

    if (value === 'cv' || value === 'travel' || value === 'interview') {
      return value;
    }

    return 'career';
  }

  private getTags(blog: any): string[] {
    const tags =
      this.currentLang === 'ar'
        ? blog.tags_ar || blog.tags_en
        : blog.tags_en || blog.tags_ar;

    if (!tags) {
      return [];
    }

    if (Array.isArray(tags)) {
      return tags.filter(Boolean);
    }

    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);

        if (Array.isArray(parsed)) {
          return parsed.filter(Boolean);
        }
      } catch {
        return tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
      }
    }

    return [];
  }

  private getBlogImage(blog: any): string {
    const image =
      blog.image_url ||
      blog.imageUrl ||
      blog.full_image_url ||
      blog.icon_url ||
      blog.iconUrl ||
      blog.image ||
      blog.icon;

    if (!image) {
      return 'assets/as/30.webp';
    }

    const imageValue = String(image);

    if (
      imageValue.startsWith('http://') ||
      imageValue.startsWith('https://') ||
      imageValue.startsWith('assets/') ||
      imageValue.startsWith('data:image')
    ) {
      return imageValue;
    }

    return `${environment.Server_URL}/${imageValue}`.replace(
      /([^:]\/)\/+/g,
      '$1'
    );
  }

  private formatDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(this.currentLang === 'ar' ? 'ar-EG' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private splitParagraphs(value: string): string[] {
    return String(value || '')
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }

  private stripHtml(value: string): string {
    if (!value) {
      return '';
    }

    return value.replace(/<[^>]*>/g, '').trim();
  }

  private setCurrentLang(forceLang?: BlogLang): void {
    const savedLang = this.isBrowser
      ? localStorage.getItem('language')
      : this.transloco.getActiveLang();

    const lang = forceLang || savedLang || this.transloco.getActiveLang();

    this.isAr = lang === 'ar';
    this.currentLang = this.isAr ? 'ar' : 'en';

    if (this.transloco.getActiveLang() !== this.currentLang) {
      this.transloco.setActiveLang(this.currentLang);
    }
  }

  private t(key: string): string {
    return this.transloco.translate(key, {}, this.currentLang);
  }
}
